import { useEffect, useRef, useState } from "react";
import { Image, Typography } from "antd";

interface TetConfig {
  avatar?: string;
  sender_name?: string;
  receiver_name?: string;
  background?: string;
  greeting?: string;
  message?: string;
  year_label?: string;
  video?: string;
  list_image?: string[];
}

interface Props {
  config: TetConfig;
}

/* ─── Falling Blossoms ─── */
const FallingBlossoms = () => {
  const petals = ["🌸", "🏵️", "✿"];
  const [flakes] = useState(() =>
    Array.from({ length: 25 }, (_, i) => ({
      id: i,
      left: Math.random() * 100,
      delay: Math.random() * 10,
      duration: 7 + Math.random() * 9,
      size: 10 + Math.random() * 14,
      opacity: 0.25 + Math.random() * 0.5,
      char: petals[Math.floor(Math.random() * petals.length)],
      swing: Math.random() > 0.5 ? "tet-swing1" : "tet-swing2",
    })),
  );

  return (
    <>
      {flakes.map((f) => (
        <div
          key={f.id}
          className="fixed pointer-events-none z-50"
          style={{
            left: `${f.left}%`,
            fontSize: `${f.size}px`,
            opacity: f.opacity,
            animation: `tet-fall ${f.duration}s linear ${f.delay}s infinite, ${f.swing} ${f.duration * 0.5}s ease-in-out ${f.delay}s infinite`,
          }}
        >
          {f.char}
        </div>
      ))}
    </>
  );
};

/* ─── Animated Section (scroll reveal) ─── */
const AnimatedSection = ({
  children,
  className = "",
  delay = 0,
}: {
  children: React.ReactNode;
  className?: string;
  delay?: number;
}) => {
  const ref = useRef<HTMLDivElement>(null);
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) setTimeout(() => setVisible(true), delay);
      },
      { threshold: 0.15 },
    );
    observer.observe(el);
    return () => observer.disconnect();
  }, [delay]);

  return (
    <div
      ref={ref}
      className={className}
      style={{
        transition: "opacity 0.8s ease, transform 0.8s ease",
        opacity: visible ? 1 : 0,
        transform: visible ? "translateY(0)" : "translateY(40px)",
      }}
    >
      {children}
    </div>
  );
};

/* ─── Hanging Lantern ─── */
const Lantern = ({
  emoji = "🏮",
  size = "2.5rem",
  lineH = 60,
  animDelay = "0s",
  style: wrapStyle = {},
}: {
  emoji?: string;
  size?: string;
  lineH?: number;
  animDelay?: string;
  style?: React.CSSProperties;
}) => (
  <div className="absolute flex flex-col items-center" style={wrapStyle}>
    <div
      className="w-px"
      style={{
        height: lineH,
        background: "linear-gradient(to bottom, rgba(255,215,0,0.6), rgba(255,215,0,0.1))",
      }}
    />
    <span
      style={{
        fontSize: size,
        animation: `tet-lantern-swing 3s ease-in-out ${animDelay} infinite`,
        filter: "drop-shadow(0 6px 20px rgba(255,50,0,0.35))",
        transformOrigin: "top center",
      }}
    >
      {emoji}
    </span>
  </div>
);

/* ─── Ornament Divider ─── */
const TetDivider = () => (
  <div className="flex items-center justify-center gap-2 py-8">
    <div className="h-px w-12" style={{ background: "linear-gradient(to right, transparent, rgba(255,215,0,0.4))" }} />
    <span className="text-base" style={{ animation: "tet-pulse 2s ease-in-out infinite" }}>
      🧧
    </span>
    <div className="h-px w-16" style={{ background: "rgba(255,215,0,0.3)" }} />
    <span className="text-sm" style={{ animation: "tet-pulse 2s ease-in-out 0.4s infinite" }}>
      🌸
    </span>
    <div className="h-px w-16" style={{ background: "rgba(255,215,0,0.3)" }} />
    <span className="text-base" style={{ animation: "tet-pulse 2s ease-in-out 0.8s infinite" }}>
      🧧
    </span>
    <div className="h-px w-12" style={{ background: "linear-gradient(to left, transparent, rgba(255,215,0,0.4))" }} />
  </div>
);

/* ═══════════════════════════════════════════
   Main Component
   ═══════════════════════════════════════════ */
const PublicTet = ({ config }: Props) => {
  return (
    <div className="min-h-screen relative overflow-x-hidden" style={{ background: "#1a0a0a" }}>
      {/* Global Tet Styles */}
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Dancing+Script:wght@400;600;700&family=Noto+Serif+Display:ital,wght@0,300;0,600;1,300&family=Be+Vietnam+Pro:ital,wght@0,300;0,400;0,600;1,300&display=swap');

        @keyframes tet-fall {
          0% { transform: translateY(-10vh) rotate(0deg); }
          100% { transform: translateY(105vh) rotate(360deg); }
        }
        @keyframes tet-swing1 {
          0%, 100% { margin-left: 0; }
          50% { margin-left: 30px; }
        }
        @keyframes tet-swing2 {
          0%, 100% { margin-left: 0; }
          50% { margin-left: -30px; }
        }
        @keyframes tet-float {
          0%, 100% { transform: translateY(0); }
          50% { transform: translateY(-10px); }
        }
        @keyframes tet-glow {
          0%, 100% { filter: brightness(1) drop-shadow(0 0 8px rgba(255,215,0,0.3)); }
          50% { filter: brightness(1.2) drop-shadow(0 0 20px rgba(255,215,0,0.6)); }
        }
        @keyframes tet-shimmer {
          0% { background-position: -200% center; }
          100% { background-position: 200% center; }
        }
        @keyframes tet-pulse {
          0%, 100% { transform: scale(1); }
          50% { transform: scale(1.15); }
        }
        @keyframes tet-lantern-swing {
          0%, 100% { transform: rotate(-3deg); }
          50% { transform: rotate(3deg); }
        }
        @keyframes tet-scroll-dot {
          0% { opacity: 1; transform: translateY(0); }
          100% { opacity: 0; transform: translateY(8px); }
        }
        @keyframes tet-twinkle {
          0%, 100% { opacity: 0.3; transform: scale(0.8); }
          50% { opacity: 1; transform: scale(1.2); }
        }
        @keyframes tet-sparkle {
          0%, 100% { opacity: 0; transform: scale(0) rotate(0deg); }
          50% { opacity: 1; transform: scale(1) rotate(180deg); }
        }
        @keyframes tet-border-glow {
          0%, 100% { border-color: rgba(255,215,0,0.15); }
          50% { border-color: rgba(255,215,0,0.35); }
        }

        .tet-gold-shimmer {
          background: linear-gradient(90deg, #ffd700 0%, #fff1a8 20%, #ffd700 40%, #ffec80 60%, #ffd700 80%, #fff1a8 100%);
          background-size: 200% auto;
          -webkit-background-clip: text;
          -webkit-text-fill-color: transparent;
          background-clip: text;
          animation: tet-shimmer 3s linear infinite;
        }

        .tet-red-gradient {
          background: linear-gradient(135deg, #8b0000 0%, #c41e3a 40%, #a0153e 70%, #6b0020 100%);
        }

        .tet-glass {
          background: linear-gradient(135deg, rgba(255,255,255,0.07) 0%, rgba(255,255,255,0.02) 100%);
          backdrop-filter: blur(12px);
          -webkit-backdrop-filter: blur(12px);
          border: 1px solid rgba(255,215,0,0.1);
        }

        .tet-glass-red {
          background: linear-gradient(135deg, rgba(200,30,30,0.12) 0%, rgba(180,20,20,0.05) 100%);
          backdrop-filter: blur(12px);
          -webkit-backdrop-filter: blur(12px);
          border: 1px solid rgba(255,50,50,0.12);
        }

        .tet-hero-overlay {
          background: linear-gradient(180deg,
            rgba(26,10,10,0) 0%,
            rgba(26,10,10,0.25) 35%,
            rgba(26,10,10,0.8) 70%,
            #1a0a0a 100%
          );
        }

        .tet-pattern-bg {
          background-image:
            radial-gradient(1.5px 1.5px at 30px 40px, rgba(255,215,0,0.15), transparent),
            radial-gradient(1px 1px at 80px 20px, rgba(255,215,0,0.1), transparent),
            radial-gradient(2px 2px at 140px 60px, rgba(255,215,0,0.12), transparent),
            radial-gradient(1px 1px at 190px 90px, rgba(255,215,0,0.08), transparent);
          background-size: 220px 110px;
        }

        .tet-gallery-img {
          transition: transform 0.4s cubic-bezier(0.25,0.46,0.45,0.94), box-shadow 0.4s ease;
        }
        .tet-gallery-img:hover {
          transform: scale(1.03);
          box-shadow: 0 16px 50px rgba(200,30,0,0.2);
        }

        .tet-envelope {
          background: linear-gradient(135deg, #c41e3a 0%, #a0153e 50%, #8b0000 100%);
          box-shadow: 0 8px 32px rgba(180,20,20,0.3), inset 0 1px 0 rgba(255,255,255,0.1);
        }
      `}</style>

      <FallingBlossoms />

      {/* ══════════ HERO SECTION ══════════ */}
      <section className="relative min-h-screen flex flex-col items-center justify-center overflow-hidden">
        {/* Background */}
        <div className="absolute inset-0">
          {config.background ? (
            <img src={config.background} alt="" className="w-full h-full object-cover" />
          ) : (
            <div
              className="w-full h-full"
              style={{
                background: "linear-gradient(160deg, #2d0a0a 0%, #1a0a0a 40%, #1a0f05 80%, #0d0500 100%)",
              }}
            />
          )}
          <div className="tet-hero-overlay absolute inset-0" />
          <div className="absolute inset-0" style={{ background: "rgba(26,10,10,0.35)" }} />
        </div>

        {/* Golden stars */}
        <div className="absolute inset-0 tet-pattern-bg opacity-50" />

        {/* Hanging Lanterns */}
        <Lantern style={{ top: 0, left: 16 }} lineH={50} size="2.2rem" animDelay="0s" />
        <Lantern style={{ top: 0, right: 24 }} lineH={75} size="2.8rem" animDelay="0.5s" />
        <Lantern style={{ top: 0, left: "35%" }} lineH={35} size="1.8rem" animDelay="1s" />
        <Lantern style={{ top: 0, right: "30%" }} lineH={55} size="2rem" animDelay="1.5s" />

        {/* Hero Content */}
        <div className="relative z-10 text-center px-6 py-20">
          {/* Floating mai blossom */}
          <div className="mb-6" style={{ animation: "tet-float 4s ease-in-out infinite" }}>
            <span className="text-5xl" style={{ animation: "tet-glow 2s ease-in-out infinite" }}>
              🌸
            </span>
          </div>

          {/* Year label / Zodiac */}
          {config.year_label && (
            <div
              className="inline-flex items-center gap-2 px-5 py-1.5 rounded-full mb-5"
              style={{
                background: "rgba(255,215,0,0.08)",
                border: "1px solid rgba(255,215,0,0.2)",
              }}
            >
              <span style={{ fontSize: "0.8rem" }}>🐍</span>
              <span
                style={{
                  fontFamily: "'Be Vietnam Pro', sans-serif",
                  fontSize: "0.75rem",
                  letterSpacing: "0.2em",
                  color: "rgba(255,215,0,0.8)",
                  textTransform: "uppercase",
                }}
              >
                {config.year_label}
              </span>
            </div>
          )}

          {/* Main greeting */}
          <h1
            className="tet-gold-shimmer leading-tight mb-3"
            style={{
              fontFamily: "'Dancing Script', cursive",
              fontSize: "clamp(2.6rem, 10vw, 3.8rem)",
            }}
          >
            {config.greeting || "Chúc Mừng Năm Mới"}
          </h1>

          {/* Sub greeting */}
          <div className="flex items-center justify-center gap-2 mb-6">
            <span style={{ color: "rgba(255,215,0,0.5)" }}>✦</span>
            <span
              style={{
                fontFamily: "'Noto Serif Display', serif",
                fontSize: "0.95rem",
                color: "rgba(255,255,255,0.55)",
                letterSpacing: "0.1em",
              }}
            >
              An Khang Thịnh Vượng
            </span>
            <span style={{ color: "rgba(255,215,0,0.5)" }}>✦</span>
          </div>

          {/* Receiver name */}
          {config.receiver_name && (
            <p
              style={{
                fontFamily: "'Be Vietnam Pro', sans-serif",
                fontSize: "1rem",
                color: "rgba(255,255,255,0.5)",
                fontStyle: "italic",
              }}
            >
              Gửi đến: <span style={{ color: "rgba(255,215,0,0.7)" }}>{config.receiver_name}</span>
            </p>
          )}

          {/* Scroll indicator */}
          <div className="mt-16 flex flex-col items-center gap-2">
            <span
              style={{
                fontFamily: "'Be Vietnam Pro', sans-serif",
                fontSize: "0.7rem",
                letterSpacing: "0.15em",
                color: "rgba(255,255,255,0.3)",
              }}
            >
              CUỘN XUỐNG
            </span>
            <div
              className="flex items-start justify-center"
              style={{
                width: 20,
                height: 32,
                borderRadius: 10,
                border: "1.5px solid rgba(255,215,0,0.25)",
                padding: 4,
              }}
            >
              <div
                style={{
                  width: 5,
                  height: 5,
                  borderRadius: "50%",
                  background: "rgba(255,215,0,0.7)",
                  animation: "tet-scroll-dot 1.5s ease-in-out infinite",
                }}
              />
            </div>
          </div>
        </div>
      </section>

      {/* ══════════ SENDER PROFILE + MESSAGE ══════════ */}
      <section className="relative py-16 px-4">
        <div className="absolute inset-0 tet-pattern-bg opacity-25" />

        <AnimatedSection className="flex flex-col items-center text-center">
          {/* Avatar */}
          {config.avatar && (
            <div className="relative mb-6">
              <div
                className="rounded-full p-1"
                style={{
                  background: "linear-gradient(135deg, rgba(255,215,0,0.5), rgba(200,30,30,0.5), rgba(255,215,0,0.5))",
                }}
              >
                <Image
                  src={config.avatar}
                  alt="avatar"
                  width={120}
                  height={120}
                  preview={false}
                  className="rounded-full object-cover"
                  style={{ border: "3px solid #1a0a0a", display: "block" }}
                />
              </div>
              {/* Decorative ring */}
              <div
                className="absolute rounded-full"
                style={{
                  inset: -10,
                  border: "1px solid rgba(255,215,0,0.15)",
                  animation: "tet-border-glow 3s ease-in-out infinite",
                }}
              />
              {/* Lucky emoji */}
              <span
                className="absolute text-xl"
                style={{ bottom: -2, right: -2, animation: "tet-float 2s ease-in-out infinite" }}
              >
                🧧
              </span>
            </div>
          )}

          {config.sender_name && (
            <>
              <span
                style={{
                  fontFamily: "'Be Vietnam Pro', sans-serif",
                  color: "rgba(255,255,255,0.35)",
                  fontSize: "0.8rem",
                  letterSpacing: "0.2em",
                  textTransform: "uppercase",
                }}
              >
                Lời chúc từ
              </span>
              <Typography.Title
                level={2}
                style={{
                  color: "#fff",
                  marginBottom: 4,
                  marginTop: 4,
                  fontFamily: "'Noto Serif Display', serif",
                }}
              >
                {config.sender_name}
              </Typography.Title>
              <div
                className="mt-2"
                style={{
                  width: 60,
                  height: 2,
                  background: "linear-gradient(to right, transparent, rgba(255,215,0,0.5), transparent)",
                }}
              />
            </>
          )}
        </AnimatedSection>

        <TetDivider />

        {/* ── Red Envelope Message Card ── */}
        {config.message && (
          <AnimatedSection delay={200}>
            <div className="max-w-md mx-auto relative">
              {/* Envelope body */}
              <div className="tet-envelope rounded-3xl relative overflow-hidden" style={{ padding: "28px 24px" }}>
                {/* Gold border inner */}
                <div
                  className="absolute rounded-2xl"
                  style={{
                    inset: 6,
                    border: "1px solid rgba(255,215,0,0.2)",
                    borderRadius: 20,
                    pointerEvents: "none",
                  }}
                />

                {/* Corner decorations */}
                <span className="absolute top-3 left-3 text-sm opacity-50">✦</span>
                <span className="absolute top-3 right-3 text-sm opacity-50">✦</span>
                <span className="absolute bottom-3 left-3 text-sm opacity-50">✦</span>
                <span className="absolute bottom-3 right-3 text-sm opacity-50">✦</span>

                {/* Gold circle seal */}
                <div className="flex justify-center mb-5">
                  <div
                    className="flex items-center justify-center"
                    style={{
                      width: 48,
                      height: 48,
                      borderRadius: "50%",
                      background: "radial-gradient(circle at 40% 35%, #ffd700, #c8a000 70%)",
                      boxShadow: "0 4px 16px rgba(255,215,0,0.3), inset 0 2px 3px rgba(255,255,255,0.3)",
                    }}
                  >
                    <span
                      style={{
                        fontFamily: "'Dancing Script', cursive",
                        fontSize: "1.3rem",
                        color: "#8b0000",
                        fontWeight: 700,
                      }}
                    >
                      福
                    </span>
                  </div>
                </div>

                {/* Message text */}
                <Typography.Paragraph
                  className="text-center"
                  style={{
                    color: "rgba(255,255,255,0.9)",
                    marginBottom: 0,
                    fontFamily: "'Be Vietnam Pro', sans-serif",
                    fontSize: "1.05rem",
                    lineHeight: 1.9,
                    fontWeight: 300,
                  }}
                >
                  "{config.message}"
                </Typography.Paragraph>

                {/* Bottom gold accent */}
                <div
                  className="mx-auto mt-5"
                  style={{
                    width: 40,
                    height: 2,
                    background: "linear-gradient(to right, transparent, rgba(255,215,0,0.5), transparent)",
                  }}
                />
              </div>

              {/* Sparkle decorations around envelope */}
              {[
                { top: -8, left: -6, delay: "0s" },
                { top: "30%", right: -10, delay: "0.8s" },
                { bottom: -6, left: "20%", delay: "1.6s" },
              ].map((pos, i) => (
                <span
                  key={i}
                  className="absolute text-xs"
                  style={{
                    ...pos,
                    color: "rgba(255,215,0,0.5)",
                    animation: `tet-sparkle 2.5s ease-in-out ${pos.delay} infinite`,
                  }}
                >
                  ✦
                </span>
              ))}
            </div>
          </AnimatedSection>
        )}
      </section>

      {/* ══════════ VIDEO ══════════ */}
      {config.video && (
        <section className="relative py-12 px-4">
          <AnimatedSection>
            <div className="text-center mb-6">
              <span
                style={{
                  fontFamily: "'Be Vietnam Pro', sans-serif",
                  color: "rgba(255,255,255,0.35)",
                  fontSize: "0.7rem",
                  letterSpacing: "0.2em",
                  textTransform: "uppercase",
                }}
              >
                Khoảnh khắc
              </span>
              <Typography.Title
                level={4}
                style={{
                  color: "#fff",
                  marginTop: 4,
                  fontFamily: "'Noto Serif Display', serif",
                }}
              >
                Video Chúc Tết 🎬
              </Typography.Title>
            </div>

            <div className="tet-glass rounded-3xl overflow-hidden max-w-md mx-auto">
              <video
                src={config.video}
                controls
                playsInline
                className="w-full"
                style={{ aspectRatio: "16/9", objectFit: "cover" }}
                poster={config.background}
              />
            </div>
          </AnimatedSection>
        </section>
      )}

      {/* ══════════ IMAGE GALLERY ══════════ */}
      {config.list_image && config.list_image.length > 0 && (
        <section className="relative py-12 px-4">
          <div className="absolute inset-0 tet-pattern-bg opacity-15" />

          <AnimatedSection className="text-center mb-8">
            <span
              style={{
                fontFamily: "'Be Vietnam Pro', sans-serif",
                color: "rgba(255,255,255,0.35)",
                fontSize: "0.7rem",
                letterSpacing: "0.2em",
                textTransform: "uppercase",
              }}
            >
              Kỷ niệm
            </span>
            <Typography.Title
              level={3}
              style={{ color: "#fff", marginTop: 4, fontFamily: "'Noto Serif Display', serif" }}
            >
              Khoảnh Khắc Ngày Tết
            </Typography.Title>
            <div className="flex items-center justify-center gap-2 mt-2">
              <div className="h-px w-8" style={{ background: "rgba(255,215,0,0.3)" }} />
              <span style={{ color: "rgba(255,215,0,0.5)", fontSize: "0.7rem" }}>🌸</span>
              <div className="h-px w-8" style={{ background: "rgba(255,215,0,0.3)" }} />
            </div>
          </AnimatedSection>

          {/* Masonry Gallery */}
          <div className="max-w-md mx-auto columns-2 gap-3" style={{ columnGap: 10 }}>
            {config.list_image.map((img, i) => (
              <AnimatedSection key={i} delay={i * 120} className="mb-3 break-inside-avoid">
                <div
                  className="tet-gallery-img rounded-2xl overflow-hidden"
                  style={{ border: "1px solid rgba(255,215,0,0.08)" }}
                >
                  <Image
                    src={img}
                    alt={`tet-${i}`}
                    className="w-full object-cover"
                    style={{
                      aspectRatio: i % 3 === 0 ? "3/4" : i % 3 === 1 ? "1/1" : "4/5",
                      display: "block",
                    }}
                  />
                </div>
              </AnimatedSection>
            ))}
          </div>
        </section>
      )}

      {/* ══════════ WISHES / CTA ══════════ */}
      <section className="relative py-20 px-4">
        <AnimatedSection className="text-center">
          {/* Animated icons */}
          <div className="mb-6 flex items-center justify-center gap-4">
            <span className="text-3xl" style={{ animation: "tet-float 3s ease-in-out infinite" }}>
              🧧
            </span>
            <span className="text-5xl" style={{ animation: "tet-float 3s ease-in-out 0.3s infinite" }}>
              🏮
            </span>
            <span className="text-3xl" style={{ animation: "tet-float 3s ease-in-out 0.6s infinite" }}>
              🧧
            </span>
          </div>

          <h3
            className="tet-gold-shimmer mb-5"
            style={{
              fontFamily: "'Dancing Script', cursive",
              fontSize: "clamp(2rem, 8vw, 2.8rem)",
            }}
          >
            Vạn Sự Như Ý!
          </h3>

          <p
            className="max-w-xs mx-auto leading-relaxed mb-10"
            style={{
              fontFamily: "'Be Vietnam Pro', sans-serif",
              fontSize: "0.95rem",
              color: "rgba(255,255,255,0.4)",
              fontWeight: 300,
              lineHeight: 1.8,
            }}
          >
            Năm mới chúc bạn luôn mạnh khoẻ, hạnh phúc, gặp nhiều may mắn và thành công!
          </p>

          {/* Decorative text tree — Mai blossom */}
          <div
            className="mb-10 select-none"
            style={{
              color: "rgba(255,215,0,0.12)",
              fontSize: "0.85rem",
              lineHeight: 1.6,
            }}
          >
            <div>🌸</div>
            <div>🌸 ✦ 🌸</div>
            <div>🌸 ✦ ✦ ✦ 🌸</div>
            <div>🌸 ✦ ✦ ✦ ✦ ✦ 🌸</div>
            <div>🌸 ✦ ✦ ✦ ✦ ✦ ✦ ✦ 🌸</div>
            <div style={{ color: "rgba(139,109,63,0.2)" }}>┃</div>
          </div>
        </AnimatedSection>
      </section>

      {/* ══════════ FOOTER ══════════ */}
      <footer className="relative py-8 px-4 text-center" style={{ borderTop: "1px solid rgba(255,215,0,0.06)" }}>
        <Typography.Text style={{ color: "rgba(255,255,255,0.18)", fontSize: "0.8rem" }}>
          Made with ❤️ • Chúc Mừng Năm Mới
        </Typography.Text>
        <div className="mt-2 flex items-center justify-center gap-1.5">
          {["🌸", "🏮", "✦", "🏮", "🌸"].map((e, i) => (
            <span
              key={i}
              className="text-xs"
              style={{ opacity: 0.3, animation: `tet-twinkle 2s ease-in-out ${i * 0.3}s infinite` }}
            >
              {e}
            </span>
          ))}
        </div>
      </footer>
    </div>
  );
};

export default PublicTet;
